"use client"

// src/react/GuideProvider.tsx
import React from "react";

// ../browser-core/src/cursor-visuals.ts
var CURSOR_COLOR = "#2f6fed";
var RIPPLE_COLOR = "#3b82f6";
var CURSOR_ELEMENT_ID = "__chatsdetCursor";
var CURSOR_HOST_TAG = "cofactor-cursor";
var GLIDE_PX_PER_MS = 1.5;
var GLIDE_MIN_MS = 120;
var GLIDE_MAX_MS = 600;
function hexToRgba(hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${n >> 16 & 255}, ${n >> 8 & 255}, ${n & 255}, ${alpha})`;
}
function buildCursorSvg() {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">
  <path d="M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z" fill="${CURSOR_COLOR}" stroke="#000" stroke-width="1.5" stroke-linejoin="round"/>
</svg>`;
}
function buildHighlightCss() {
  return `
  @keyframes chatsdet-ripple {
    0% { transform: translate(-50%, -50%) scale(0.4); opacity: 1; }
    100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
  }
  @keyframes chatsdet-halo-in {
    0% { opacity: 0; transform: scale(1.06); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes chatsdet-glow {
    0% { box-shadow: 0 0 8px 3px rgba(59, 130, 246, 0.95); }
    50% { box-shadow: 0 0 24px 8px rgba(59, 130, 246, 0.85); }
    100% { box-shadow: 0 0 8px 3px rgba(59, 130, 246, 0.95); }
  }
  .chatsdet-highlight {
    position: fixed;
    pointer-events: none;
    z-index: 999999;
  }
  .chatsdet-highlight--click {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${hexToRgba(RIPPLE_COLOR, 0.45)};
    border: 4px solid ${RIPPLE_COLOR};
    animation: chatsdet-ripple 380ms ease-out forwards;
  }
  .chatsdet-highlight--type {
    border-radius: 4px;
    border: 3px solid ${RIPPLE_COLOR};
    background: rgba(59, 130, 246, 0.18);
    animation: chatsdet-glow 520ms ease-in-out;
  }
  .chatsdet-highlight--hover {
    border: 3px dashed #8b5cf6;
    background: rgba(139, 92, 246, 0.2);
    border-radius: 4px;
  }
  .chatsdet-highlight--halo {
    border-radius: 6px;
    border: 3px solid ${RIPPLE_COLOR};
    background: ${hexToRgba(RIPPLE_COLOR, 0.14)};
    animation: chatsdet-halo-in 140ms ease-out;
  }
`;
}
function buildKeycapCss() {
  return `
  @keyframes chatsdet-keycap-press {
    0%   { transform: translateY(0);   box-shadow: 0 4px 0 0 rgba(15, 23, 42, 0.55); }
    45%  { transform: translateY(3px); box-shadow: 0 1px 0 0 rgba(15, 23, 42, 0.55); }
    100% { transform: translateY(0);   box-shadow: 0 4px 0 0 rgba(15, 23, 42, 0.55); }
  }
  @keyframes chatsdet-keycap-fade {
    0%, 70% { opacity: 1; }
    100%    { opacity: 0; }
  }
  .chatsdet-keycap-tray {
    position: fixed;
    left: 50%;
    bottom: 32px;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    pointer-events: none;
    animation: chatsdet-keycap-fade 800ms ease-out forwards;
  }
  .chatsdet-keycap {
    min-width: 34px;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid rgba(15, 23, 42, 0.75);
    background: linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%);
    color: #0f172a;
    font: 600 14px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
    text-align: center;
    animation: chatsdet-keycap-press 180ms ease-out;
  }
`;
}

// src/cursor/renderer.ts
var GUIDE_CHROME_CSS = `
  @keyframes guide-attention {
    0%   { transform: translate(-50%, -50%) scale(0.9); opacity: 0.55; }
    70%  { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
    100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
  }
  .guide-pulse {
    position: fixed;
    transition-property: left, top;
    transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
    width: 34px;
    height: 34px;
    margin-left: 8px;
    margin-top: 8px;
    border-radius: 50%;
    border: 2px solid ${CURSOR_COLOR};
    opacity: 0.9;
    pointer-events: none;
    animation: guide-attention 1.8s ease-out infinite;
  }
  .guide-hint {
    position: fixed;
    transition-property: left, top;
    transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
    /* Centred under the arrow, like the ring. Paired with the clamp in moveTo,
       which keeps it on screen at the right edge \u2014 a hint that runs off the
       viewport teaches nobody anything, and that is its whole job. */
    transform: translate(-50%, 26px);
    max-width: 90vw;
    padding: 3px 8px;
    border-radius: 5px;
    background: rgba(15, 23, 42, 0.92);
    color: #f8fafc;
    font: 500 11px/1.4 ui-sans-serif, system-ui, -apple-system, sans-serif;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.3);
  }
  @media (prefers-reduced-motion: reduce) {
    .guide-pulse { animation: none; opacity: 0.6; transform: translate(-50%, -50%); }
  }
`;
var HINT_TEXT = "Esc to take over";
var HINT_EDGE_MARGIN_PX = 8;
function sleepUntil(ms, signal) {
  if (signal?.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener("abort", done, { once: true });
  });
}
function createCursor(signal, doc = document) {
  let destroyed = false;
  let root = null;
  let host = null;
  let arrow = null;
  let pulse = null;
  let hint = null;
  let x = 0;
  let y = 0;
  const transient = /* @__PURE__ */ new Set();
  function ensure() {
    if (destroyed) return null;
    if (root) return root;
    if (!doc.body) return null;
    const newHost = doc.createElement(CURSOR_HOST_TAG);
    newHost.setAttribute("aria-hidden", "true");
    newHost.setAttribute("inert", "");
    newHost.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;margin:0;padding:0;border:0;overflow:visible;pointer-events:none;z-index:2147483647;";
    const newRoot = newHost.attachShadow({ mode: "closed" });
    const style = doc.createElement("style");
    style.textContent = buildHighlightCss() + buildKeycapCss() + GUIDE_CHROME_CSS;
    newRoot.appendChild(style);
    const newArrow = doc.createElement("div");
    newArrow.id = CURSOR_ELEMENT_ID;
    newArrow.innerHTML = buildCursorSvg();
    newArrow.style.cssText = "position:fixed;pointer-events:none;left:0px;top:0px;transition-property:left,top;transition-timing-function:cubic-bezier(0.25,0.1,0.25,1);transition-duration:120ms";
    newRoot.appendChild(newArrow);
    const newPulse = doc.createElement("div");
    newPulse.className = "guide-pulse";
    newRoot.appendChild(newPulse);
    const newHint = doc.createElement("div");
    newHint.className = "guide-hint";
    newHint.textContent = HINT_TEXT;
    newRoot.appendChild(newHint);
    doc.body.appendChild(newHost);
    host = newHost;
    arrow = newArrow;
    pulse = newPulse;
    hint = newHint;
    root = newRoot;
    return newRoot;
  }
  function track(node, ttlMs) {
    const entry = {
      node,
      timer: setTimeout(() => {
        transient.delete(entry);
        node.parentNode?.removeChild(node);
      }, ttlMs)
    };
    transient.add(entry);
  }
  function glideMsTo(nx, ny) {
    const dist = Math.hypot(nx - x, ny - y);
    return Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, Math.round(dist / GLIDE_PX_PER_MS)));
  }
  return {
    async moveTo(nx, ny) {
      const r = ensure();
      if (!r || !arrow) return;
      const duration = glideMsTo(nx, ny);
      arrow.style.transitionDuration = `${duration}ms`;
      arrow.style.left = `${nx}px`;
      arrow.style.top = `${ny}px`;
      if (pulse) {
        pulse.style.transitionDuration = `${duration}ms`;
        pulse.style.left = `${nx}px`;
        pulse.style.top = `${ny}px`;
      }
      if (hint) {
        const half = hint.offsetWidth / 2 + HINT_EDGE_MARGIN_PX;
        hint.style.transitionDuration = `${duration}ms`;
        hint.style.left = `${Math.min(Math.max(nx, half), window.innerWidth - half)}px`;
        hint.style.top = `${ny}px`;
      }
      x = nx;
      y = ny;
      await sleepUntil(duration, signal);
    },
    halo(rect) {
      const r = ensure();
      if (!r) return;
      const node = doc.createElement("div");
      node.className = "chatsdet-highlight chatsdet-highlight--halo";
      node.style.left = `${rect.left - 4}px`;
      node.style.top = `${rect.top - 4}px`;
      node.style.width = `${rect.width + 8}px`;
      node.style.height = `${rect.height + 8}px`;
      r.appendChild(node);
      track(node, 900);
    },
    ripple(rx, ry) {
      const r = ensure();
      if (!r) return;
      const node = doc.createElement("div");
      node.className = "chatsdet-highlight chatsdet-highlight--click";
      node.style.left = `${rx}px`;
      node.style.top = `${ry}px`;
      r.appendChild(node);
      track(node, 600);
    },
    keys(labels) {
      const r = ensure();
      if (!r || labels.length === 0) return;
      const tray = doc.createElement("div");
      tray.className = "chatsdet-keycap-tray";
      for (const label of labels.slice(0, 8)) {
        const cap = doc.createElement("div");
        cap.className = "chatsdet-keycap";
        cap.textContent = label;
        tray.appendChild(cap);
      }
      r.appendChild(tray);
      track(tray, 900);
    },
    destroy() {
      destroyed = true;
      for (const entry of transient) clearTimeout(entry.timer);
      transient.clear();
      host?.parentNode?.removeChild(host);
      root = null;
      host = null;
      arrow = null;
      pulse = null;
      hint = null;
    }
  };
}

// src/core/keymap.ts
var SHIFTED = new Set('~!@#$%^&*()_+{}|:"<>?'.split(""));
function needsShift(char) {
  return SHIFTED.has(char) || char.length === 1 && char !== char.toLowerCase();
}
var NAMED = {
  Enter: "Enter",
  Tab: "Tab",
  Escape: "Escape",
  Backspace: "Backspace",
  Delete: "Delete",
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight",
  Home: "Home",
  End: "End",
  PageUp: "PageUp",
  PageDown: "PageDown",
  " ": "Space"
};
function codeForChar(char) {
  if (/^[a-z]$/i.test(char)) return `Key${char.toUpperCase()}`;
  if (/^[0-9]$/.test(char)) return `Digit${char}`;
  return "";
}
function keyDescriptor(key) {
  const named = NAMED[key];
  if (named) return { key: key === " " ? " " : key, code: named, shiftKey: false };
  if (key.length === 1) return { key, code: codeForChar(key), shiftKey: needsShift(key) };
  return { key, code: "", shiftKey: false };
}

// src/core/dispatch.ts
var GUIDE_EVENT = /* @__PURE__ */ Symbol.for("runcofactor.guide.event");
var MOUSE_POINTER_ID = 1;
function mark(event) {
  Object.defineProperty(event, GUIDE_EVENT, { value: true, enumerable: false });
  return event;
}
function mouseInit(pt, detail, buttons) {
  return {
    bubbles: true,
    cancelable: true,
    // Required to cross a shadow boundary — several component libraries render
    // their interactive element inside one.
    composed: true,
    view: typeof window === "undefined" ? null : window,
    clientX: pt.clientX,
    clientY: pt.clientY,
    screenX: pt.clientX,
    screenY: pt.clientY,
    detail,
    button: 0,
    buttons
  };
}
function pointerInit(pt, buttons, pressure) {
  return {
    ...mouseInit(pt, 0, buttons),
    pointerId: MOUSE_POINTER_ID,
    pointerType: "mouse",
    isPrimary: true,
    width: 1,
    height: 1,
    pressure,
    tiltX: 0,
    tiltY: 0,
    twist: 0
  };
}
function fire(target, event) {
  return !target.dispatchEvent(mark(event));
}
var raf = () => new Promise((resolve) => {
  if (typeof requestAnimationFrame === "undefined") {
    setTimeout(resolve, 16);
    return;
  }
  requestAnimationFrame(() => resolve());
});
async function settle() {
  await raf();
  await raf();
}
function focusableFrom(el) {
  let node = el;
  while (node) {
    if (node instanceof HTMLElement && (node.tabIndex >= 0 || node.isContentEditable)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
async function clickAt(pt, opts = {}) {
  const doc = opts.doc ?? document;
  const target = doc.elementFromPoint(pt.clientX, pt.clientY) ?? doc.body;
  fire(target, new PointerEvent("pointerover", pointerInit(pt, 0, 0)));
  fire(target, new MouseEvent("mouseover", mouseInit(pt, 0, 0)));
  fire(target, new PointerEvent("pointermove", pointerInit(pt, 0, 0)));
  fire(target, new MouseEvent("mousemove", mouseInit(pt, 0, 0)));
  fire(target, new PointerEvent("pointerdown", pointerInit(pt, 1, 0.5)));
  const mousedownPrevented = fire(target, new MouseEvent("mousedown", mouseInit(pt, 1, 1)));
  if (!mousedownPrevented) {
    focusableFrom(target)?.focus({ preventScroll: true });
  }
  await settle();
  fire(target, new PointerEvent("pointerup", pointerInit(pt, 0, 0)));
  fire(target, new MouseEvent("mouseup", mouseInit(pt, 0, 0)));
  if (opts.signal?.aborted) return { defaultPrevented: false, aborted: true };
  const defaultPrevented = fire(
    target,
    new PointerEvent("click", { ...pointerInit(pt, 0, 0), detail: 1 })
  );
  return { defaultPrevented, aborted: false };
}
var nativeSetters = (() => {
  const get = (proto) => Object.getOwnPropertyDescriptor(proto, "value")?.set;
  return {
    input: typeof HTMLInputElement === "undefined" ? void 0 : get(HTMLInputElement.prototype),
    textarea: typeof HTMLTextAreaElement === "undefined" ? void 0 : get(HTMLTextAreaElement.prototype),
    select: typeof HTMLSelectElement === "undefined" ? void 0 : get(HTMLSelectElement.prototype)
  };
})();
function isValueElement(el) {
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
}
function setNativeValue(el, value) {
  const setter = el instanceof HTMLInputElement ? nativeSetters.input : el instanceof HTMLTextAreaElement ? nativeSetters.textarea : nativeSetters.select;
  if (setter) setter.call(el, value);
  else el.value = value;
}
var PER_CHAR_DELAY_MS = 18;
var GuideAborted = class extends Error {
  constructor() {
    super("guide run aborted");
    this.name = "GuideAborted";
  }
};
var sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function typeInto(el, text, opts = {}) {
  const { clearFirst = false, signal } = opts;
  const throwIfAborted = () => {
    if (signal?.aborted) throw new GuideAborted();
  };
  throwIfAborted();
  el.focus({ preventScroll: true });
  if (clearFirst && el.value !== "") {
    setNativeValue(el, "");
    fire(
      el,
      new InputEvent("input", {
        bubbles: true,
        composed: true,
        inputType: "deleteContentBackward"
      })
    );
  }
  let defaultPrevented = false;
  for (const char of Array.from(text)) {
    throwIfAborted();
    const desc = keyDescriptor(char);
    fire(
      el,
      new KeyboardEvent("keydown", {
        bubbles: true,
        cancelable: true,
        composed: true,
        key: desc.key,
        code: desc.code,
        shiftKey: desc.shiftKey
      })
    );
    const rejected = fire(
      el,
      new InputEvent("beforeinput", {
        bubbles: true,
        cancelable: true,
        composed: true,
        inputType: "insertText",
        data: char
      })
    );
    if (rejected) {
      defaultPrevented = true;
    } else {
      const next = el.value + char;
      setNativeValue(el, next);
      if ("setSelectionRange" in el && el.type !== "email" && el.type !== "number") {
        try {
          el.setSelectionRange(next.length, next.length);
        } catch {
        }
      }
      fire(
        el,
        new InputEvent("input", {
          bubbles: true,
          composed: true,
          inputType: "insertText",
          data: char
        })
      );
    }
    fire(
      el,
      new KeyboardEvent("keyup", {
        bubbles: true,
        cancelable: true,
        composed: true,
        key: desc.key,
        code: desc.code,
        shiftKey: desc.shiftKey
      })
    );
    await sleep(PER_CHAR_DELAY_MS);
  }
  return { defaultPrevented };
}
function pressKey(key, doc = document) {
  const desc = keyDescriptor(key);
  const target = doc.activeElement ?? doc.body;
  const init = {
    bubbles: true,
    cancelable: true,
    composed: true,
    key: desc.key,
    code: desc.code,
    shiftKey: desc.shiftKey
  };
  const defaultPrevented = fire(target, new KeyboardEvent("keydown", init));
  fire(target, new KeyboardEvent("keyup", init));
  return { defaultPrevented };
}

// src/core/resolve.ts
var EXCLUDE_ATTR = "data-guide-exclude";
function isExcluded(el) {
  return el.closest(`[${EXCLUDE_ATTR}]`) !== null;
}
function inspect(surface) {
  const root = surface.element;
  if (!root.isConnected) return "not_in_dom";
  if (isExcluded(root)) return "excluded";
  const el = surface.target ? surface.target(root) : root;
  if (!el) return "no_target";
  if (!el.isConnected) return "not_in_dom";
  if (isExcluded(el)) return "excluded";
  const style = getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") {
    return "not_visible";
  }
  if (style.pointerEvents === "none") return "not_visible";
  if (el.matches(":disabled") || el.getAttribute("aria-disabled") === "true") return "disabled";
  if (el.closest("[inert]")) return "disabled";
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return "zero_size";
  return void 0;
}
async function resolveForAction(surface) {
  const early = inspect(surface);
  if (early) return { ok: false, refusal: early };
  const root = surface.element;
  const el = surface.target ? surface.target(root) : root;
  el.scrollIntoView({ block: "center", inline: "center", behavior: "instant" });
  await settle();
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return { ok: false, refusal: "zero_size" };
  const point = { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 };
  if (point.clientX < 0 || point.clientY < 0 || point.clientX > window.innerWidth || point.clientY > window.innerHeight) {
    return { ok: false, refusal: "off_screen" };
  }
  const hit = document.elementFromPoint(point.clientX, point.clientY);
  if (!hit || !(el.contains(hit) || hit.contains(el))) {
    const blocker = hit instanceof Element ? describeBlocker(hit) : "nothing";
    return { ok: false, refusal: "obscured", detail: `covered by ${blocker}` };
  }
  return { ok: true, element: el, point };
}
function describeBlocker(el) {
  const role = el.getAttribute("role");
  const label = el.getAttribute("aria-label");
  return [el.tagName.toLowerCase(), role && `role=${role}`, label && `"${label}"`].filter(Boolean).join(" ");
}

// src/core/driver.ts
var DEFAULT_WAIT_MS = 5e3;
var MAX_WAIT_MS = 3e4;
var POLL_MS = 100;
function buildTree(surfaces) {
  const ordered = [...surfaces].sort((a, b) => {
    const position = a.element.compareDocumentPosition(b.element);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
  const open = [];
  return ordered.map((surface) => {
    while (open.length > 0 && !open[open.length - 1].element.contains(surface.element)) {
      open.pop();
    }
    const parent = open[open.length - 1];
    open.push(surface);
    return parent ? { surface, parentId: parent.id } : { surface };
  });
}
function observePage(registry, extra) {
  const surfaces = buildTree(registry.list()).map(({ surface, parentId }) => {
    const blocked = inspect(surface);
    return {
      id: surface.id,
      role: surface.role,
      label: surface.label,
      ...surface.description ? { description: surface.description } : {},
      ...parentId ? { parentId } : {},
      // `describe()` is skipped entirely when the surface is blocked, and
      // `blocked` includes `excluded`. Otherwise `data-guide-exclude` would
      // gate actions but not observation, and a host that put a masked field
      // or an account switcher behind it would still have that state
      // serialized into the payload a model reads. Exclusion has to mean
      // "cannot see" as well as "cannot touch", or it is not worth having.
      ...surface.describe && !blocked ? { state: safeDescribe(surface) } : {},
      ...blocked ? { blocked } : {}
    };
  });
  return {
    url: typeof location === "undefined" ? "" : location.href,
    title: typeof document === "undefined" ? "" : document.title,
    surfaces,
    ...extra
  };
}
function createDriver(deps) {
  const observe = (extra) => observePage(deps.registry, extra);
  const fail = (refusal, detail) => ({
    ok: false,
    refusal,
    ...detail ? { detail } : {},
    observed: observe()
  });
  function targetOf(surface) {
    return surface.target ? surface.target(surface.element) : surface.element;
  }
  async function execute(op) {
    if (deps.signal.aborted) return fail("stopped");
    switch (op.action) {
      case "snapshot":
        return { ok: true, observed: observe() };
      case "read_text": {
        const surface = deps.registry.get(op.surfaceId);
        if (!surface) return fail("unknown_surface");
        const blocked = inspect(surface);
        if (blocked) return fail(blocked);
        const el = targetOf(surface);
        if (!el) return fail("no_target");
        return { ok: true, observed: observe({ text: isValueElement(el) ? el.value : el.innerText }) };
      }
      case "click": {
        const surface = deps.registry.get(op.surfaceId);
        if (!surface) return fail("unknown_surface");
        const resolved = await resolveForAction(surface);
        if (!resolved.ok) return fail(resolved.refusal, resolved.detail);
        if (deps.signal.aborted) return fail("stopped");
        await deps.cursor.moveTo(resolved.point.clientX, resolved.point.clientY);
        if (deps.signal.aborted) return fail("stopped");
        deps.cursor.halo(resolved.element.getBoundingClientRect());
        const outcome = await clickAt(resolved.point, { signal: deps.signal });
        if (outcome.aborted) return fail("stopped", "released without activating");
        deps.cursor.ripple(resolved.point.clientX, resolved.point.clientY);
        return { ok: true, observed: observe({ defaultPrevented: outcome.defaultPrevented }) };
      }
      case "type": {
        const surface = deps.registry.get(op.surfaceId);
        if (!surface) return fail("unknown_surface");
        const resolved = await resolveForAction(surface);
        if (!resolved.ok) return fail(resolved.refusal, resolved.detail);
        if (!isValueElement(resolved.element)) {
          return fail("not_a_text_field", `${resolved.element.tagName.toLowerCase()} has no value`);
        }
        await deps.cursor.moveTo(resolved.point.clientX, resolved.point.clientY);
        if (deps.signal.aborted) return fail("stopped");
        deps.cursor.halo(resolved.element.getBoundingClientRect());
        try {
          const { defaultPrevented } = await typeInto(resolved.element, op.text, {
            clearFirst: op.clearFirst ?? false,
            signal: deps.signal
          });
          return { ok: true, observed: observe({ defaultPrevented }) };
        } catch (error) {
          if (error instanceof GuideAborted) return fail("stopped");
          throw error;
        }
      }
      case "press_key": {
        const focused = document.activeElement;
        if (focused && isExcluded(focused)) {
          return fail("excluded", "focus is inside an excluded region");
        }
        deps.cursor.keys([op.key]);
        const { defaultPrevented } = pressKey(op.key);
        return { ok: true, observed: observe({ defaultPrevented }) };
      }
      case "wait_for":
        return waitFor(op);
    }
  }
  async function waitFor(op) {
    if (!op.surfaceId && !op.text) {
      return fail("bad_request", "wait_for needs a surfaceId or text");
    }
    const budget = Math.min(op.timeoutMs ?? DEFAULT_WAIT_MS, MAX_WAIT_MS);
    const deadline = Date.now() + budget;
    const want = op.gone !== true;
    const satisfied = () => {
      if (op.surfaceId) {
        const surface = deps.registry.get(op.surfaceId);
        const present2 = Boolean(surface && !inspect(surface));
        return present2 === want;
      }
      const present = (document.body?.innerText ?? "").includes(op.text);
      return present === want;
    };
    while (Date.now() < deadline) {
      if (deps.signal.aborted) return fail("stopped");
      if (satisfied()) return { ok: true, observed: observe() };
      await new Promise((r) => setTimeout(r, POLL_MS));
    }
    return satisfied() ? { ok: true, observed: observe() } : fail("timed_out");
  }
  return { execute, observe };
}
function safeDescribe(surface) {
  try {
    return surface.describe?.();
  } catch (error) {
    console.warn(`[guide] describe() threw for surface ${JSON.stringify(surface.id)}`, error);
    return void 0;
  }
}

// src/core/registry.ts
function createRegistry() {
  const surfaces = /* @__PURE__ */ new Map();
  return {
    register(decl, element) {
      const existing = surfaces.get(decl.id);
      if (existing && existing.element !== element && existing.element.isConnected) {
        console.warn(
          `[guide] duplicate surface id ${JSON.stringify(decl.id)} \u2014 two mounted elements claim it; the later one wins and the earlier is unreachable to the agent.`
        );
      }
      surfaces.set(decl.id, { ...decl, element });
      return () => {
        if (surfaces.get(decl.id)?.element === element) surfaces.delete(decl.id);
      };
    },
    get: (id) => surfaces.get(id),
    list: () => [...surfaces.values()],
    size: () => surfaces.size
  };
}

// src/core/preempt.ts
var IGNORED_KEYS = /* @__PURE__ */ new Set(["Shift", "Control", "Alt", "Meta", "CapsLock"]);
var WHEEL_THRESHOLD_PX = 12;
var WATCHED = ["pointerdown", "keydown", "wheel", "touchstart"];
function isRealUserEvent(event) {
  return event.isTrusted && !(GUIDE_EVENT in event);
}
function watchForEscape(onEscape) {
  const handler = (event) => {
    if (!isRealUserEvent(event)) return;
    if (event.key !== "Escape") return;
    onEscape("escape");
  };
  window.addEventListener("keydown", handler, { capture: true });
  return () => window.removeEventListener("keydown", handler, true);
}
function watchForHumanInput(onPreempt) {
  const handler = (event) => {
    if (!isRealUserEvent(event)) return;
    if (event.type === "keydown" && IGNORED_KEYS.has(event.key)) return;
    if (event.type === "wheel") {
      const wheel = event;
      if (Math.abs(wheel.deltaY) < WHEEL_THRESHOLD_PX && Math.abs(wheel.deltaX) < WHEEL_THRESHOLD_PX) {
        return;
      }
    }
    onPreempt("human_input");
  };
  for (const type of WATCHED) {
    window.addEventListener(type, handler, { capture: true, passive: true });
  }
  return () => {
    for (const type of WATCHED) window.removeEventListener(type, handler, true);
  };
}

// src/react/context.ts
import { createContext, useContext } from "react";
var GuideContext = createContext(null);
function useGuide() {
  const value = useContext(GuideContext);
  if (!value) throw new Error("useGuide must be used inside <GuideProvider>");
  return value;
}

// src/react/GuideProvider.tsx
import { jsx } from "react/jsx-runtime";
function GuideProvider({ children, enabled = true, onAbort }) {
  const registry = React.useMemo(() => createRegistry(), []);
  const cursorRef = React.useRef(null);
  const controllerRef = React.useRef(null);
  const inFlightRef = React.useRef(false);
  const haltedRef = React.useRef(false);
  const haltReasonRef = React.useRef(null);
  const [sessionOpen, setSessionOpen] = React.useState(false);
  const [isDriving, setIsDriving] = React.useState(false);
  const [lastAbort, setLastAbort] = React.useState(null);
  const onAbortRef = React.useRef(onAbort);
  onAbortRef.current = onAbort;
  const stop = React.useCallback((reason = "user_stop") => {
    if (!controllerRef.current) return;
    haltedRef.current = true;
    haltReasonRef.current = reason;
    setLastAbort(reason);
    onAbortRef.current?.(reason);
    controllerRef.current.abort();
    controllerRef.current = null;
    cursorRef.current?.destroy();
    cursorRef.current = null;
    inFlightRef.current = false;
    setIsDriving(false);
    setSessionOpen(false);
  }, []);
  const stopRef = React.useRef(stop);
  stopRef.current = stop;
  const start = React.useCallback(() => {
    haltedRef.current = false;
    haltReasonRef.current = null;
    setLastAbort(null);
  }, []);
  React.useEffect(() => {
    if (!sessionOpen) return;
    return watchForEscape((reason) => stopRef.current(reason));
  }, [sessionOpen]);
  React.useEffect(() => {
    if (!enabled) stopRef.current("host_disabled");
  }, [enabled]);
  React.useEffect(() => () => stopRef.current("user_stop"), []);
  const execute = React.useCallback(
    async (op) => {
      const refuse = (detail, blind = false) => ({
        ok: false,
        refusal: "stopped",
        detail,
        // A real observation even on refusal: an agent told "no" still needs to
        // know what is on the page, and an empty surface list reads as "this
        // page has nothing" rather than "you are not allowed". The exception is
        // the kill switch, below.
        observed: blind ? { url: "", title: "", surfaces: [] } : observePage(registry)
      });
      if (!enabled) {
        return refuse("the host has the guide switched off", true);
      }
      if (haltedRef.current) {
        const takenByUser = haltReasonRef.current === "escape" || haltReasonRef.current === "human_input";
        return refuse(
          takenByUser ? "the user took the controls back; do not resume until they hand them over" : "this driving session is closed; the host must open a new one"
        );
      }
      if (inFlightRef.current) {
        return refuse("another operation is already running");
      }
      if (!controllerRef.current) {
        controllerRef.current = new AbortController();
        cursorRef.current = createCursor(controllerRef.current.signal);
        setSessionOpen(true);
      }
      const controller = controllerRef.current;
      const cursor = cursorRef.current;
      inFlightRef.current = true;
      setIsDriving(true);
      const detachInputWatch = watchForHumanInput((reason) => stopRef.current(reason));
      try {
        const driver = createDriver({ registry, cursor, signal: controller.signal });
        return await driver.execute(op);
      } catch (error) {
        stopRef.current("error");
        return refuse(error instanceof Error ? error.message : "the operation failed");
      } finally {
        detachInputWatch();
        inFlightRef.current = false;
        setIsDriving(false);
      }
    },
    [enabled, registry]
  );
  const value = React.useMemo(
    () => ({ registry, isDriving, sessionOpen, lastAbort, execute, stop, start }),
    [registry, isDriving, sessionOpen, lastAbort, execute, stop, start]
  );
  return /* @__PURE__ */ jsx(GuideContext.Provider, { value, children });
}

// src/react/useGuideSurface.ts
import React2 from "react";
function useGuideSurface(declaration) {
  const { registry } = useGuide();
  const latest = React2.useRef(declaration);
  latest.current = declaration;
  const unregister = React2.useRef(null);
  const { id } = declaration;
  const ref = React2.useCallback(
    (element) => {
      unregister.current?.();
      unregister.current = null;
      if (!element) return;
      unregister.current = registry.register(
        {
          ...latest.current,
          // `describe` and `target` must observe the CURRENT render's closure,
          // not the one captured when the element first mounted.
          describe: latest.current.describe ? () => latest.current.describe() : void 0,
          target: latest.current.target ? (root) => latest.current.target(root) : void 0
        },
        element
      );
    },
    // `id` only: a changed id is a genuinely different surface and must
    // re-register. Everything else is read live through `latest`.
    [registry, id]
  );
  React2.useEffect(() => () => unregister.current?.(), []);
  return { ref };
}

// src/react/Guide.tsx
import React3 from "react";
function childRefOf(child) {
  const props = child.props;
  if (props.ref) return props.ref;
  const legacy = child;
  return legacy.ref;
}
function assignRef(ref, value) {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}
function Guide({
  children,
  ...declaration
}) {
  const { ref } = useGuideSurface(declaration);
  const attached = React3.useRef(false);
  const { id } = declaration;
  React3.useEffect(() => {
    if (attached.current) return;
    console.warn(
      `[guide] <Guide id=${JSON.stringify(id)}> never received an element. Its child must forward \`ref\` to a DOM node \u2014 wrap the child in React.forwardRef, or use useGuideSurface() and place the ref yourself.`
    );
  }, [id]);
  const child = React3.Children.only(children);
  if (!React3.isValidElement(child)) {
    throw new Error(`<Guide id=${JSON.stringify(id)}> needs a single element child.`);
  }
  const childRef = childRefOf(child);
  const merged = React3.useCallback(
    (element) => {
      attached.current = element !== null;
      assignRef(childRef, element);
      ref(element);
    },
    [childRef, ref]
  );
  return React3.cloneElement(child, {
    ref: merged
  });
}

// src/core/render.ts
function renderValue(value) {
  if (value === null) return "null";
  if (typeof value === "string") {
    return value === "" || /[\s"=]/.test(value) ? JSON.stringify(value) : value;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return JSON.stringify(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "?";
  }
}
function renderState(state) {
  if (!state) return "";
  const pairs = Object.entries(state).filter(([, value]) => value !== void 0).map(([key, value]) => `${key}=${renderValue(value)}`);
  return pairs.length > 0 ? ` ${pairs.join(" ")}` : "";
}
function renderSurface(surface, depth) {
  const pad = "  ".repeat(depth);
  const head = [
    surface.id,
    surface.role,
    JSON.stringify(surface.label),
    renderState(surface.state).trimStart(),
    surface.blocked ? `[blocked: ${surface.blocked}]` : ""
  ].filter(Boolean).join(" ");
  const lines = [`${pad}${head}`];
  if (surface.description) lines.push(`${pad}  # ${surface.description}`);
  return lines;
}
function depthsOf(surfaces) {
  const byId = new Map(surfaces.map((surface) => [surface.id, surface]));
  const depths = /* @__PURE__ */ new Map();
  for (const surface of surfaces) {
    let depth = 0;
    let cursor = surface.parentId;
    const seen = /* @__PURE__ */ new Set([surface.id]);
    while (cursor && byId.has(cursor) && !seen.has(cursor)) {
      seen.add(cursor);
      depth += 1;
      cursor = byId.get(cursor).parentId;
    }
    depths.set(surface.id, depth);
  }
  return depths;
}
function renderObservation(observation) {
  const lines = [];
  const heading = [observation.title, observation.url].filter(Boolean).join(" \xB7 ");
  if (heading) lines.push(heading);
  if (observation.surfaces.length === 0) {
    lines.push("", "No controls are declared on this page.");
  } else {
    lines.push("");
    const depths = depthsOf(observation.surfaces);
    for (const surface of observation.surfaces) {
      lines.push(...renderSurface(surface, depths.get(surface.id) ?? 0));
    }
  }
  if (observation.text !== void 0) {
    lines.push("", `text: ${JSON.stringify(observation.text)}`);
  }
  if (observation.defaultPrevented) {
    lines.push("", "note: the page called preventDefault on that interaction");
  }
  return lines.join("\n");
}
export {
  EXCLUDE_ATTR,
  Guide,
  GuideProvider,
  renderObservation,
  useGuide,
  useGuideSurface
};
//# sourceMappingURL=index.js.map